import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  forwardRef,
  inject,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';

export interface SelectOption {
  label: string;
  value: unknown;
  subtext?: string;
  imageUrl?: string;
}

export interface SelectOptionGroup {
  heading: string;
  options: SelectOption[];
}

type SelectSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './select.html',
  styles: [`
    :host {
      display: contents;
    }
    .app-select-root {
      overflow: visible !important;
      position: relative;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent implements ControlValueAccessor, OnDestroy {
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);
  private standaloneValue: unknown = '';
  private cvaDisabled = false;
  private onChange: (value: unknown) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private _options: Array<string | SelectOption> = [];
  private _upward = false;

  @Input() form?: FormGroup;
  @Input() controlName = '';
  @Input() control?: FormControl | AbstractControl | null;
  @Input() label = '';
  @Input() labelClass = 'mb-1 block text-sm font-medium text-slate900';
  @Input() placeholder = 'Select an option';
  @Input() set options(value: readonly (string | SelectOption)[] | null | undefined) {
    this._options = Array.isArray(value) ? [...value] : [];
  }
  get options(): Array<string | SelectOption> {
    return this._options;
  }
  @Input() optionGroups: SelectOptionGroup[] = [];
  @Input() error = '';
  @Input() emptyMessage = '';
  @Input() disabled = false;
  @Input() required = false;
  @Input() size: SelectSize = 'lg';
  @Input() upward = false;
  @Input() position: 'top' | 'bottom' | 'auto' = 'auto';
  @Input() multiple = false;
  @Input() disableWhenEmpty = true;
  @Input() searchable = false;
  @Input() clearable = false;
  @Input() showCheckbox = false;
  isOpen = false;
  searchQuery = '';
 
  @ViewChild('triggerButton') private triggerButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('dropdownMenu') private dropdownMenu?: ElementRef<HTMLDivElement>;
 
  ngOnDestroy(): void {
    this.closeMenu();
  }
 
  get normalizedOptions(): SelectOption[] {
    return this.options.map((option) =>
      typeof option === 'string' ? { label: option, value: option } : option,
    );
  }
 
  get filteredOptions(): SelectOption[] {
    if (!this.searchable || !this.searchQuery.trim()) {
      return this.normalizedOptions;
    }
    const q = this.searchQuery.trim().toLowerCase();
    return this.normalizedOptions.filter((o) => o.label.toLowerCase().includes(q));
  }
 
  get currentControl(): AbstractControl | null {
    if (this.control) {
      return this.control;
    }

    if (!this.form || !this.controlName) {
      return null;
    }

    return this.form.get(this.controlName);
  }
 
  get selectedValue(): unknown {
    return this.currentControl ? this.currentControl.value : this.standaloneValue;
  }
 
  get selectedValues(): unknown[] {
    if (!this.multiple) return [];
    const value = this.selectedValue;
    return Array.isArray(value) ? value : [];
  }

  get selectedLabel(): string {
    if (this.multiple) {
      const selected = this.normalizedOptions
        .filter((option) => this.selectedValues.includes(option.value))
        .map((option) => option.label);
      return selected.join(', ');
    }
    const selected = this.normalizedOptions.find((option) => option.value === this.selectedValue);
    return selected?.label ?? '';
  }

  get selectedOption(): SelectOption | undefined {
    if (this.multiple) return undefined;
    return this.normalizedOptions.find((option) => option.value === this.selectedValue);
  }
 
  get shouldShowError(): boolean {
    const control = this.currentControl;
    if (!control) {
      return !!this.error;
    }

    return !!this.error || (control.invalid && (control.dirty || control.touched));
  }
 
  get fallbackError(): string {
    const control = this.currentControl;
    if (!control?.errors) {
      return '';
    }

    const serverError = this.resolveTextError(control.errors['server'] ?? control.errors['api']);
    if (serverError) {
      return serverError;
    }

    if (control.errors['required']) {
      return `${this.label || 'This field'} is required.`;
    }

    if (control.errors['singleLifeOnly']) {
      return `${this.label || 'This field'} must be a single life (First or Second only).`;
    }

    if (control.errors['oneOf']) {
      return `${this.label || 'This field'} must be one of: ${control.errors['oneOf'].allowed.join(', ')}.`;
    }

    return 'Invalid value.';
  }
 
  get hasOptions(): boolean {
    return this.filteredOptions.length > 0;
  }
 
  get isEmptyDisabled(): boolean {
    return this.disableWhenEmpty && !this.hasOptions;
  }
 
  get resolvedEmptyMessage(): string {
    const customMessage = this.emptyMessage.trim();

    if (customMessage) {
      return customMessage;
    }

    const fieldName = this.label.trim() || 'options';
    return `No ${fieldName} available`;
  }
 
  dropdownStyle: Record<string, string> = {};

  private readonly scrollListener = (): void => {
    if (this.isOpen) {
      this.updatePosition();
    }
  };

  private readonly outsideClickListener = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Node) || !this.hostElement.nativeElement.contains(target)) {
      this.closeMenu();
      this.cdr.detectChanges();
    }
  };

  private bindScrollListener(): void {
    document.addEventListener('scroll', this.scrollListener, { capture: true, passive: true });
    window.addEventListener('resize', this.scrollListener, { passive: true });
    // Capture phase so a child elsewhere calling stopPropagation can't keep the menu open.
    document.addEventListener('pointerdown', this.outsideClickListener, true);
  }

  private unbindScrollListener(): void {
    document.removeEventListener('scroll', this.scrollListener, true);
    window.removeEventListener('resize', this.scrollListener);
    document.removeEventListener('pointerdown', this.outsideClickListener, true);
  }

  updatePosition(): void {
    if (!this.triggerButton) {
      return;
    }
    const rect = this.triggerButton.nativeElement.getBoundingClientRect();
    
    let targetPosition: 'top' | 'bottom' = 'bottom';
    
    if (this.upward || this.position === 'top') {
      targetPosition = 'top';
    } else if (this.position === 'bottom') {
      targetPosition = 'bottom';
    } else {
      // Auto collision detection: open upward if there's not enough room below and there's more room above
      const dropdownHeight = this.dropdownMenu?.nativeElement?.offsetHeight || 280;
      const spaceBelow = window.innerHeight - rect.bottom - 4;
      const spaceAbove = rect.top - 4;
      
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        targetPosition = 'top';
      } else {
        targetPosition = 'bottom';
      }
    }
    
    if (targetPosition === 'top') {
      this.dropdownStyle = {
        position: 'fixed',
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        bottom: `${window.innerHeight - rect.top + 4}px`,
        top: 'auto',
        zIndex: '99999',
        opacity: '1',
        pointerEvents: 'auto',
      };
    } else {
      this.dropdownStyle = {
        position: 'fixed',
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        top: `${rect.bottom + 4}px`,
        bottom: 'auto',
        zIndex: '99999',
        opacity: '1',
        pointerEvents: 'auto',
      };
    }

    this.cdr.detectChanges();
  }

  toggle(): void {
    if (this.isDisabled) {
      return;
    }
  
    if (this.isOpen) {
      this.closeMenu();
      return;
    }
  
    // Initialize with fixed positioning and hide it to avoid causing a layout flash or scrollbars
    this.dropdownStyle = {
      position: 'fixed',
      opacity: '0',
      pointerEvents: 'none',
      zIndex: '99999',
    };
    this.isOpen = true;
    this.searchQuery = '';

    // Synchronously trigger render so the dropdown is added to the DOM and can be measured immediately
    this.cdr.detectChanges();

    this.updatePosition();
    this.bindScrollListener();
  }

  private shouldOpenUpward(): boolean {
    const element = this.hostElement.nativeElement;
    const rect = element.getBoundingClientRect();
    const dropdownHeight = 250;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    return spaceBelow < dropdownHeight;
  }
 
  clearValue(event: MouseEvent): void {
    event.stopPropagation();
    if (this.isDisabled) {
      return;
    }
    
    const control = this.currentControl;
    if (!control) {
      this.updateValue(this.multiple ? [] : '');
      return;
    }

    control.setValue(this.multiple ? [] : '');
    control.markAsDirty();
    control.markAsTouched();
    this.updateValue(this.multiple ? [] : '');
  }

  selectOption(option: SelectOption): void {
    if (this.isEmptyDisabled) {
      return;
    }

    const control = this.currentControl;
    if (!control) {
      this.updateValue(option.value);
      this.closeMenu();
      return;
    }

    if (this.multiple) {
      const currentValues = this.selectedValues;
      const exists = currentValues.some((value) => value === option.value);
      const nextValues = exists
        ? currentValues.filter((value) => value !== option.value)
        : [...currentValues, option.value];
      control.setValue(nextValues);
      control.markAsDirty();
      control.markAsTouched();
      this.updateValue(nextValues);
      return;
    }

    control.setValue(option.value);
    control.markAsDirty();
    control.markAsTouched();
    this.updateValue(option.value);
    this.closeMenu();
  }

  isOptionSelected(option: SelectOption): boolean {
    if (this.multiple) {
      return this.selectedValues.includes(option.value);
    }
    return this.selectedValue === option.value;
  }
 
  get triggerClassMap(): Record<string, boolean> {
    return {
      'h-[46px]': this.size === 'lg',
      'h-[40px]': this.size === 'md',
      'h-9': this.size === 'sm',
      'border-red500': this.shouldShowError,
      'border-slate300': !this.shouldShowError,
      'cursor-not-allowed bg-slate100 text-slate500': this.isDisabled || this.isEmptyDisabled,
      'bg-slate50': !this.isDisabled && !this.isEmptyDisabled,
    };
  }
 
  get triggerTextSizeClass(): string {
    return this.size === 'sm' ? 'text-xs' : 'text-sm';
  }
 
  get isDisabled(): boolean {
    return this.disabled || this.cvaDisabled || !!this.currentControl?.disabled;
  }
 
  writeValue(value: unknown): void {
    this.standaloneValue = value;
  }
 
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }
 
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
 
  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled = isDisabled;
  }
 
  private updateValue(value: unknown): void {
    this.standaloneValue = value;
    this.onChange(value);
    this.onTouched();
  }

  private closeMenu(): void {
    this.isOpen = false;
    this.unbindScrollListener();
  }

  private resolveTextError(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }
}
